import Box from '@mui/material/Box'
import Radio from '@mui/material/Radio'
import {
  choiceDraftFromLegacy,
  toggleSingleSelectId,
} from '../../../lib/choice-answer'
import { resolveDisplayOptions } from '../../../lib/survey-option-display'
import type { ChoiceDraftAnswer, SurveyDraftAnswer, SurveyQuestion } from '../../../lib/survey-question.types'
import {
  surveyCheckboxControlSx,
  surveyCheckboxOptionLabelSx,
  surveyCheckboxRowSelectedSx,
  surveyCheckboxRowSx,
} from '../../../styles/surveyExecutionStyles'
import { ChoiceOptionLayout } from './ChoiceOptionLayout'
import { ChoiceTextSpecify } from './OtherSpecifyField'

interface SingleSelectQuestionProps {
  readonly question: SurveyQuestion
  readonly value: SurveyDraftAnswer
  readonly displaySeed: string
  readonly onChange: (value: ChoiceDraftAnswer) => void
}

export function SingleSelectQuestion({
  question,
  value,
  displaySeed,
  onChange,
}: SingleSelectQuestionProps) {
  const draft = choiceDraftFromLegacy(question, value)
  const options = resolveDisplayOptions(question, displaySeed)
  const selectedSet = new Set(draft.selectedIds)

  const pick = (optionId: string) => {
    const nextIds = toggleSingleSelectId(draft.selectedIds, optionId)
    onChange({ selectedIds: nextIds, otherText: draft.otherText })
  }

  return (
    <Box>
      <ChoiceOptionLayout
        layoutHorizontal={question.layoutHorizontal}
        multiColumn={question.multiColumn}
        data-testid={`survey-q-${question.id}-options`}
      >
        {options.map((opt) => {
          const selected = selectedSet.has(opt.id)
          return (
            <Box
              key={opt.id}
              component="button"
              type="button"
              role="radio"
              aria-checked={selected}
              data-testid={`survey-q-${question.id}-option-${opt.id}`}
              onClick={() => pick(opt.id)}
              sx={{
                ...surveyCheckboxRowSx,
                ...(selected ? surveyCheckboxRowSelectedSx : {}),
              }}
            >
              <Radio
                checked={selected}
                value={opt.id}
                tabIndex={-1}
                disableRipple
                sx={surveyCheckboxControlSx}
              />
              <Box component="span" sx={surveyCheckboxOptionLabelSx}>
                {opt.label}
              </Box>
            </Box>
          )
        })}
      </ChoiceOptionLayout>
      <ChoiceTextSpecify question={question} draft={draft} onChange={onChange} />
    </Box>
  )
}
